"use client";

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { carSearchReducer, initialState } from "@/reducers/carSearchReducer";
import carSearchSchema from "@/schemas/carSearchSchema";
import carServices from "@/services/carServices";
import { staticDataService } from "@/services/staticService";
import { formatDate } from "@/utils/dateFormatter";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useReducer, useRef } from "react";

const DEBOUNCE_DELAY = 300; // ms

export const useCarSearch = (initialData = {}) => {
  // Initial data ile state'i merge et
  const mergedInitialState = {
    ...initialState,
    ...initialData,
  };

  const [state, dispatch] = useReducer(carSearchReducer, mergedInitialState);
  const locale = useLocale();
  const t = useTranslationDetector("carSearch");
  const autocompleteTimeoutRef = useRef(null);

  const setLoading = (loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };

  const setPickUpDate = (pickUpDate) => {
    dispatch({ type: "SET_PICKUP_DATE", payload: pickUpDate });
  };

  const setDropOffDate = (dropOffDate) => {
    dispatch({ type: "SET_DROPOFF_DATE", payload: dropOffDate });
  };

  const setPickUpTime = (pickUpTime) => {
    dispatch({ type: "SET_PICKUP_TIME", payload: pickUpTime });
  };

  const setDropOffTime = (dropOffTime) => {
    dispatch({ type: "SET_DROPOFF_TIME", payload: dropOffTime });
  };

  const setIsDifferentLocation = (isDifferentLocation) => {
    dispatch({
      type: "SET_IS_DIFFERENT_LOCATION",
      payload: isDifferentLocation,
    });
  };

  const setDriverAge = (driverAge) => {
    dispatch({ type: "SET_DRIVER_AGE", payload: driverAge });
  };

  const setPickUpLocation = useCallback((location) => {
    dispatch({ type: "SET_PICKUP_LOCATION", payload: location });
  }, []);

  const setDropOffLocation = useCallback((location) => {
    dispatch({ type: "SET_DROPOFF_LOCATION", payload: location });
  }, []);

  const setLatestCarSearches = (latestCarSearches) => {
    dispatch({
      type: "SET_LATEST_CAR_SEARCHES",
      payload: latestCarSearches,
    });
  };

  const setPopularLocations = (popularLocations) => {
    dispatch({ type: "SET_POPULAR_LOCATIONS", payload: popularLocations });
  };

  const setPublicHolidays = (publicHolidays) => {
    dispatch({ type: "SET_PUBLIC_HOLIDAYS", payload: publicHolidays });
  };

  // Bus için sadece gerekli fonksiyonları kullan
  const getPopularLocations = async () => {
    try {
      const popularCitiesResponse =
        await carServices.search.getPopularLocations({
          LanguageCode: locale.toUpperCase(),
        });

      setPopularLocations(
        (popularCitiesResponse || []).map((item) => ({
          ...item,
          id: item.Id,
          label: item.Value,
          value: item.Value,
        })),
      );
    } catch (error) {
      console.error(
        "getPopularCities: Popular cities yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const fetchAutocomplete = async (query, locale) => {
    const response = await carServices.search.autocomplete(query, locale);

    return (response || []).map((item) => ({
      ...item,
      id: item.Id,
      label: item.Value,
      value: item.Value,
    }));
  };

  // Lazy-load flags and loaders
  const ensurePopularLoaded = useCallback(async () => {
    try {
      // Load if missing only
      if (!state.popularLocations) {
        await getPopularLocations();
      }
    } catch (_) {
      // no-op, errors already logged inside fetchers
    }
  }, [state.popularLocations]);

  const searchForCarLocations = useCallback(
    (query) => {
      if (!query || query.length < 2) {
        return Promise.resolve(null);
      }

      if (autocompleteTimeoutRef.current) {
        clearTimeout(autocompleteTimeoutRef.current);
      }

      return new Promise((resolve, reject) => {
        autocompleteTimeoutRef.current = setTimeout(async () => {
          try {
            const response = await fetchAutocomplete(query, locale);
            resolve(response);
          } catch (err) {
            reject(err);
          }
        }, DEBOUNCE_DELAY);
      });
    },
    [locale, carServices],
  );

  // Şehir kodundan şehir bilgisi getiren fonksiyon
  const getCityInfoByCode = useCallback(
    async (cityCode) => {
      try {
        const response = await fetchAutocomplete(cityCode, locale);

        // Response'dan ilk uygun sonucu bul
        if (response && response.length > 0) {
          // Tam eşleşme yoksa ilk sonucu döndür
          return response?.[0] || null;
        }

        return null;
      } catch (error) {
        console.error(
          `getCityInfoByCode: ${cityCode} için şehir bilgisi getirilemedi:`,
          error,
        );
        return null;
      }
    },
    [locale],
  );

  const onSelectPickUpLocation = useCallback(
    (location) => {
      setPickUpLocation(location);
    },
    [setPickUpLocation],
  );

  const onSelectDropOffLocation = useCallback(
    (location) => {
      setDropOffLocation(location);
    },
    [setDropOffLocation],
  );

  // Utility functions
  const formatDateForUrl = useCallback((date, time) => {
    return `${formatDate(date, { format: "YYYY-MM-DD" })}T${time}`;
  }, []);

  const generateQuery = (searchData) => {
    // Flight search ile aynı formatta tek parametre olarak birleştir
    let locationSlug = searchData.pickUpLocation?.Id;

    if (searchData.isDifferentLocation && searchData.dropOffLocation) {
      locationSlug += `_${searchData.dropOffLocation.Id}`;
    }

    const pickupDateTime = formatDateForUrl(
      searchData.pickUpDateRaw || searchData.pickUpDate,
      searchData.pickUpTime,
    );
    const dropOffDateTime = formatDateForUrl(
      searchData.dropOffDateRaw || searchData.dropOffDate,
      searchData.dropOffTime,
    );

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    queryParams.set("driverAge", searchData.driverAge.toString());

    Object.keys(initialData?.defaultFilters).forEach((key) => {
      queryParams.set(key, initialData?.defaultFilters[key]);
    });

    const queryString = queryParams.toString();
    const baseUrl = `/car-rental/search/${locationSlug}/${pickupDateTime}_${dropOffDateTime}`;

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  };

  const generateCarSearchUrl = useCallback(() => {
    return generateQuery(state);
  }, [
    state.pickUpLocation,
    state.dropOffLocation,
    state.pickUpDate,
    state.dropOffDate,
    state.pickUpTime,
    state.dropOffTime,
    state.driverAge,
    state.isDifferentLocation,
    formatDateForUrl,
    initialData.defaultFilters,
  ]);

  // Validation
  const validateForm = useCallback(() => {
    const formData = {
      pickUpLocation: state.pickUpLocation,
      dropOffLocation: state.dropOffLocation,
      isDifferentLocation: state.isDifferentLocation,
    };

    try {
      carSearchSchema.parse(formData);
      return {};
    } catch (error) {
      const errors = {};
      error.errors.forEach((err) => {
        errors[err.path[0]] = t(err.message);
      });
      return errors;
    }
  }, [state.pickUpLocation, state.dropOffLocation, state.isDifferentLocation]);

  const getPublicHolidays = async () => {
    try {
      const publicHolidaysResponse = await staticDataService.getStaticData({
        LanguageCode: locale.toUpperCase(),
        PageName: "PublicHolidays",
        GroupNames: [
          new Date().getMonth() === 11
            ? new Date().getFullYear() + 1
            : new Date().getFullYear(),
        ],
      });
      const publicHolidays = JSON.parse(
        publicHolidaysResponse?.ContentParts[0]?.ContentGroupKeys[0]
          ?.ContentGroupValues[0]?.Value,
      ).map((item) => ({
        date: item.date,
        title: item.localName,
      }));
      setPublicHolidays(publicHolidays);
    } catch (error) {
      console.error(
        "getPublicHolidays: Public holidays yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getCarDefaultOrigin = async () => {
    try {
      const defaultCityOrigin = (t("defaultCityOrigin") || "").trim();

      if (!defaultCityOrigin) {
        return;
      }

      const response = await fetchAutocomplete(defaultCityOrigin, locale);
      setPickUpLocation(response?.[0] || null);
    } catch (error) {
      console.error(
        "getCarDefaultOrigin: Car default origin yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getLocationCityText = (location) => {
    const parts = [];

    if (location?.district) {
      parts.push(location.district);
    }

    if (location?.city) {
      parts.push(location.city);
    }

    return parts.join(", ");
  };

  const getLatestCarSearches = () => {
    const STORAGE_KEY = initialData.storageKey || "latestCarSearches";
    const latestCarSearches = localStorage.getItem(STORAGE_KEY);
    if (latestCarSearches) {
      const parsedSearches = JSON.parse(latestCarSearches);
      // Eğer 3'ten fazla arama varsa, son 3 tanesini al
      const limitedSearches =
        parsedSearches.length > 3 ? parsedSearches.slice(0, 3) : parsedSearches;
      setLatestCarSearches(limitedSearches);
    }
  };

  useEffect(() => {
    getLatestCarSearches();

    if (
      !(
        initialData.initialPickUpLocationCode ||
        initialData.initialPickUpLocation
      ) &&
      !initialData.defaultOriginEmpty &&
      locale
    ) {
      getCarDefaultOrigin();
    }
  }, [locale]);

  // Initial data'da şehir kodları varsa API'den gerçek şehir bilgilerini getir
  useEffect(() => {
    const loadInitialLocationData = async () => {
      try {
        // Departure city kodunu kontrol et
        if (initialData.initialPickUpLocationCode && !state.pickUpLocation) {
          const locationData = await getCityInfoByCode(
            initialData.initialPickUpLocationCode,
          );
          if (locationData) {
            setPickUpLocation(locationData);
          }
        }

        if (
          initialData.initialDropOffLocationCode &&
          initialData.initialDropOffLocationCode !==
            initialData.initialPickUpLocationCode &&
          !state.dropOffLocation
        ) {
          const locationData = await getCityInfoByCode(
            initialData.initialDropOffLocationCode,
          );
          if (locationData) {
            setDropOffLocation(locationData);

            setIsDifferentLocation(true);
          }
        }

        if (initialData.initialPickUpLocation && !state.pickUpLocation) {
          setPickUpLocation(initialData.initialPickUpLocation);
        }

        if (
          initialData.initialDropOffLocation &&
          initialData.initialDropOffLocation.Id !==
            initialData.initialPickUpLocation.Id &&
          !state.dropOffLocation
        ) {
          setDropOffLocation(initialData.initialDropOffLocation);
          setIsDifferentLocation(true);
        }
      } catch (error) {
        console.error(
          "loadInitialLocationData: Initial şehir bilgileri yüklenirken hata oluştu:",
          error,
        );
      }
    };

    // Sadece locale yüklendiyse çalıştır
    if (
      locale &&
      (initialData.initialPickUpLocationCode ||
        initialData.initialPickUpLocation)
    ) {
      loadInitialLocationData();
    }
  }, [locale, initialData.initialPickUpLocationCode]);

  return {
    // Car-specific state mapping
    pickUpLocation: state.pickUpLocation,
    dropOffLocation: state.dropOffLocation,
    pickUpDate: state.pickUpDate,
    dropOffDate: state.dropOffDate,
    pickUpTime: state.pickUpTime,
    dropOffTime: state.dropOffTime,
    driverAge: state.driverAge,
    isDifferentLocation: state.isDifferentLocation,
    latestCarSearches: state.latestCarSearches,
    popularLocations: state.popularLocations,
    publicHolidays: state.publicHolidays,

    // Actions
    setPickUpLocation,
    setDropOffLocation,
    setPickUpDate,
    setDropOffDate,
    setPickUpTime,
    setDropOffTime,
    setIsDifferentLocation,
    setDriverAge,

    // Service functions
    searchForCarLocations,
    onSelectPickUpLocation,
    onSelectDropOffLocation,
    ensurePopularLoaded,

    // Utility functions
    validateForm,
    generateCarSearchUrl,
    refreshPublicHolidays: getPublicHolidays,
    getLocationCityText,
    getLatestCarSearches,
    generateQuery,

    // State for compatibility
    state,
  };
};
